import {
  Dialog,
  Button,
  Typography,
  Box,
  TextField,
  Snackbar,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import React, { useEffect, useMemo, useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ja } from "date-fns/locale";
import { AmazonSettingDialogStyle as styles } from "./AmazonSettingDialogStyle";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { pendingShipmentUpdatedFlagState } from "../recoil/atoms/pendingShipmentAtoms";
import { baseSearchOptionsSelector } from "../recoil/selectors/baseSearchOptionsSelector";
import BaseFilterSelect from "./BaseFilter";
import {
  RequestLWAStartAuthRequest,
  RequestUpdateAmazonInfoRequest,
} from "../services/OpenAPI";
import { loginUserSelector } from "../recoil/selectors/loginUserSelector";
import { useApiUpdateAmazonInfo } from "../hooks/api/useApiUpdateAmazonInfo";
import { useApiStartLwaAuth } from "../hooks/api/useApiStartLwaAuth";

export type AmazonSettingItem = {
  sellerId: string;
  mwsToken: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onUpdate: (updatedUser: AmazonSettingItem, baseIds: number[]) => void;
  statusId: number;
};

const AmazonSettingDialog: React.FC<Props> = ({ open, onClose, onUpdate }) => {
  const setPendingShipmentUpdateFlag = useSetRecoilState(
    pendingShipmentUpdatedFlagState
  );
  const loginUser = useRecoilValue(loginUserSelector);
  const {
    updateAmazonInfo,
    result: registerDbRersult,
    setResult: setRegisterDbRersult,
  } = useApiUpdateAmazonInfo();
  const { startLwaAuth } = useApiStartLwaAuth();

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">(
    "success"
  );
  const handleCloseToast = () => setToastOpen(false);

  const [sellerId, setSellerId] = useState("");
  const [mwsToken, setMwsToken] = useState("");
  const [editInfo, setEditInfo] = useState<AmazonSettingItem>();

  const resetFields = () => {
    setSellerId("");
    setMwsToken("");
    setEditInfo(undefined);
  };

  // 入力欄データまとめ（拠点除く）
  const inputItems: [
    string,
    string,
    React.Dispatch<React.SetStateAction<string>>,
    keyof AmazonSettingItem
  ][] = [
    ["出品者ID", sellerId, setSellerId, "sellerId"],
    ["MWS認証トークン", mwsToken, setMwsToken, "mwsToken"],
  ];

  const handleUpdateAmazonInfo = async () => {
    const req: RequestUpdateAmazonInfoRequest = {
      tenantId: loginUser?.tenantId ?? 0,
      userId: loginUser?.id ?? 0,
      sellerId: sellerId,
      mwsToken: mwsToken,
    };
    await updateAmazonInfo(req);
  };

  const getEnv = () => {
    const hostname = window.location.hostname;
    if (hostname === "fba.rakulogi.com") {
      return "prod";
    } else {
      return "staging";
    }
  };

  const handleConnectAmazon = async () => {
    const req: RequestLWAStartAuthRequest = {
      tenantId: loginUser?.tenantId ?? 0,
      userId: loginUser?.id ?? 0,
      env: getEnv(),
    };
    const res = await startLwaAuth(req);

    if (res?.authorize_url) {
      // window.open(res.authorize_url, "_blank", "noopener,noreferrer"); // ← ここでAmazonの承認画面へ飛ばす
      window.location.href = res.authorize_url; // ← ここでAmazonの承認画面へ飛ばす
    } else {
      // エラーハンドリング
    }
  };

  useEffect(() => {
    if (!registerDbRersult) return;
    setRegisterDbRersult(false);
    handleConnectAmazon();
  }, [registerDbRersult]);

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
        <Dialog
          open={open}
          onClose={(event, reason) => {
            if (reason !== "backdropClick") onClose();
          }}
          disableEnforceFocus
          maxWidth={false}
          fullWidth={false}
          PaperProps={{
            sx: {
              display: "flex",
              width: "700px",
              height: "330px",
              maxWidth: "none",
              maxHeight: "none",
              overflowY: "auto",
            },
          }}
        >
          <Box sx={styles.header}>
            <Typography sx={styles.headerText}>Amazon設定</Typography>
            <Button
              onClick={() => {
                resetFields();
                onClose();
              }}
              sx={styles.closeButton}
            >
              ×
            </Button>
          </Box>

          <Box sx={styles.formArea}>
            {inputItems.map(([label, value, setter, key]) => (
              <Box key={label} sx={styles.inputRow}>
                <Typography sx={styles.label}>{label}：</Typography>
                <TextField
                  size="medium"
                  value={value}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setter(newValue);
                    setEditInfo((prev) => ({
                      ...prev!,
                      [key]: newValue,
                    }));
                  }}
                  sx={{ width: "350px" }}
                />
              </Box>
            ))}
          </Box>

          <Button
            onClick={() => {
              // onUpdate(editInfo!, selectedBaseIds);
              handleUpdateAmazonInfo();
            }}
            variant="contained"
            sx={styles.floatingRegisterButton}
          >
            更新
          </Button>

          <Snackbar
            open={toastOpen}
            autoHideDuration={4000}
            onClose={handleCloseToast}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <MuiAlert
              onClose={handleCloseToast}
              severity={toastSeverity}
              sx={{ width: "100%" }}
              elevation={6}
              variant="filled"
            >
              {toastMessage}
            </MuiAlert>
          </Snackbar>
        </Dialog>
      </LocalizationProvider>
    </>
  );
};

export default AmazonSettingDialog;
